import React, { useState, useEffect } from 'react';
import { 
  BarChart, Bar, PieChart, Pie, Cell, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer 
} from 'recharts';

// Import mock data
import { 
  productCategoriesL1, 
  productCategoriesL2, 
  productCategoriesL3, 
  products 
} from '../../data/productData';
import { classificationStandards } from '../../data/standardsData';

// Import custom components
import PathNavigation from '../common/PathNavigation';
import VisualizationControls from '../common/VisualizationControls';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#8DD1E1', '#A4DE6C', '#D0ED57', '#FFAA33'];

const ProductHierarchy = () => {
  const [selectedStandard, setSelectedStandard] = useState(classificationStandards[0]);
  const [selectedCategoryL1, setSelectedCategoryL1] = useState(null);
  const [selectedCategoryL2, setSelectedCategoryL2] = useState(null);
  const [selectedCategoryL3, setSelectedCategoryL3] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedMetric, setSelectedMetric] = useState('totalSales');
  const [visualizationType, setVisualizationType] = useState('piechart');
  const [currentLevel, setCurrentLevel] = useState(1); // Track the current hierarchy level
  const [pathHistory, setPathHistory] = useState([]); // Track navigation path for breadcrumbs
  const [availableL1Categories, setAvailableL1Categories] = useState([]);
  const [availableL2Categories, setAvailableL2Categories] = useState([]);
  const [availableL3Categories, setAvailableL3Categories] = useState([]);
  const [availableProducts, setAvailableProducts] = useState([]);
  
  // Initialize with available categories on component mount
  useEffect(() => {
    // For POC, we'll display the top 3 categories in our data model
    // Electronics (id=1), Clothing (id=2), and Beauty (id=5)
    const topCategories = productCategoriesL1.filter(cat => cat.id === 1 || cat.id === 2 || cat.id === 5);
    setAvailableL1Categories(topCategories);
  }, []);
  
  // Update L2 categories when L1 selection changes
  useEffect(() => {
    if (selectedCategoryL1) {
      // Get the top 3 L2 categories for the selected L1 category
      // For each L1 (Electronics, Clothing, Beauty), we show their top 3 subcategories
      let subcategories = [];
      
      if (selectedCategoryL1 === 1) { // Electronics
        subcategories = productCategoriesL2.filter(cat => 
          cat.id === 101 || cat.id === 102 || cat.id === 103
        );
      } else if (selectedCategoryL1 === 2) { // Clothing
        subcategories = productCategoriesL2.filter(cat => 
          cat.id === 201 || cat.id === 202 || cat.id === 203
        );
      } else if (selectedCategoryL1 === 5) { // Beauty
        subcategories = productCategoriesL2.filter(cat => 
          cat.id === 501 || cat.id === 502 || cat.id === 503
        );
      }
      
      setAvailableL2Categories(subcategories);
    } else {
      setAvailableL2Categories([]);
    }
  }, [selectedCategoryL1]);

  
  // Update L3 categories when L2 selection changes
  useEffect(() => {
    if (selectedCategoryL2) {
      // Get exactly 3 L3 categories for the selected L2 category
      let l3Categories = [];
      
      // Electronics subcategories
      if (selectedCategoryL2 === 101) { // Smartphones
        l3Categories = productCategoriesL3.filter(cat => 
          cat.id === 1001 || cat.id === 1002 || cat.id === 1003
        );
      } else if (selectedCategoryL2 === 102) { // Laptops
        l3Categories = productCategoriesL3.filter(cat => 
          cat.id === 1011 || cat.id === 1012 || cat.id === 1013
        );
      } else if (selectedCategoryL2 === 103) { // Televisions
        l3Categories = productCategoriesL3.filter(cat => 
          cat.id === 1021 || cat.id === 1022 || cat.id === 1023
        );
      }
      
      // Clothing subcategories
      else if (selectedCategoryL2 === 201) { // Casual Wear
        l3Categories = productCategoriesL3.filter(cat => 
          cat.id === 2001 || cat.id === 2002 || cat.id === 2003
        );
      } else if (selectedCategoryL2 === 202) { // Formal Wear
        l3Categories = productCategoriesL3.filter(cat => 
          cat.id === 2011 || cat.id === 2012 || cat.id === 2013
        );
      } else if (selectedCategoryL2 === 203) { // Sportswear
        l3Categories = productCategoriesL3.filter(cat => 
          cat.id === 2021 || cat.id === 2022 || cat.id === 2023
        );
      }
      
      // Beauty subcategories
      else if (selectedCategoryL2 === 501) { // Skincare
        l3Categories = productCategoriesL3.filter(cat => 
          cat.id === 5001 || cat.id === 5002 || cat.id === 5003
        );
      } else if (selectedCategoryL2 === 502) { // Makeup
        l3Categories = productCategoriesL3.filter(cat => 
          cat.id === 5011 || cat.id === 5012 || cat.id === 5013
        );
      } else if (selectedCategoryL2 === 503) { // Haircare
        l3Categories = productCategoriesL3.filter(cat => 
          cat.id === 5021 || cat.id === 5022 || cat.id === 5023
        );
      }
      
      setAvailableL3Categories(l3Categories);
    } else {
      setAvailableL3Categories([]);
    }
  }, [selectedCategoryL2]);
  
  // Update products when L3 selection changes
  useEffect(() => {
    if (selectedCategoryL3) {
      // Get exactly 3 products for the selected L3 category
      const productsForL3 = products
        .filter(product => product.categoryL3Id === selectedCategoryL3)
        .slice(0, 3); // Always get exactly 3 products per category
      
      setAvailableProducts(productsForL3);
    } else {
      setAvailableProducts([]);
    }
  }, [selectedCategoryL3]);
  
  // Get current data based on hierarchy level
  const getCurrentLevelData = () => {
    switch(currentLevel) {
      case 1:
        return availableL1Categories;
      case 2:
        return availableL2Categories;
      case 3:
        return availableL3Categories;
      case 4:
        return availableProducts;
      default:
        return availableL1Categories;
    }
  };
  
  // Prepare data for pie chart visualization
  const preparePieChartData = () => {
    const data = getCurrentLevelData();
    
    if (!data || data.length === 0) {
      return [];
    }
    
    return data.map(item => ({
      name: item.name,
      value: item[selectedMetric] || 0, // Handle case where product might not have all metrics
      id: item.id
    }));
  };
  
  // Render pie chart
  const renderPieChart = () => {
    const data = preparePieChartData();
    
    if (!data || data.length === 0) {
      return <div className="text-center py-12 text-gray-500">No data available for the selected categories</div>;
    }
    
    return (
      <ResponsiveContainer width="100%" height={500}>
        <PieChart>
          <Pie
            data={data}
            cx="50%"
            cy="50%"
            labelLine={true}
            label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(1)}%`}
            outerRadius={180}
            fill="#8884d8"
            dataKey="value"
            onClick={(data) => handlePieClick(data)}
            cursor="pointer"
          >
            {data.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
            ))}
          </Pie>
          <Tooltip formatter={(value) => 
            selectedMetric === 'totalSales' 
              ? `$${(value/1000000).toFixed(2)}M` 
              : selectedMetric === 'growth' 
                ? `${value.toFixed(1)}%` 
                : value
          } />
          <Legend onClick={(entry) => handleLegendClick(entry)} />
        </PieChart>
      </ResponsiveContainer>
    );
  };
  
  // Handle pie segment click for drill down
  const handlePieClick = (data) => {
    if (!data || !data.payload) return;
    
    const clickedItem = data.payload;
    const clickedId = clickedItem.id;
    
    // Find the matching category/product based on current level
    switch(currentLevel) {
      case 1:
        drillDown(clickedId, null, null, null, 2);
        break;
      case 2:
        drillDown(selectedCategoryL1, clickedId, null, null, 3);
        break;
      case 3:
        drillDown(selectedCategoryL1, selectedCategoryL2, clickedId, null, 4);
        break;
      case 4:
        // At product level, we don't drill down further
        break;
    }
  };
  
  // Handle legend click
  const handleLegendClick = (entry) => {
    if (!entry || !entry.value) return;
    
    const clickedName = entry.value;
    const currentData = getCurrentLevelData();
    const item = currentData.find(d => d.name === clickedName);
    
    if (item) {
      // Simulate a click on the pie segment
      handlePieClick({ payload: { id: item.id, name: item.name } });
    }
  };
  
  // Render bar chart
  const renderBarChart = () => {
    const data = getCurrentLevelData();
    
    if (!data || data.length === 0) {
      return <div className="text-center py-12 text-gray-500">No data available for the selected categories</div>;
    }
    
    return (
      <ResponsiveContainer width="100%" height={500}>
        <BarChart
          data={data}
          margin={{ top: 20, right: 30, left: 30, bottom: 60 }}
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="name" angle={-45} textAnchor="end" height={70} />
          <YAxis />
          <Tooltip formatter={(value) => 
            selectedMetric === 'totalSales' 
              ? `$${(value/1000000).toFixed(2)}M` 
              : selectedMetric === 'growth' 
                ? `${value.toFixed(1)}%` 
                : value
          } />
          <Legend />
          <Bar 
            dataKey={selectedMetric} 
            fill="#0088FE" 
            name={
              selectedMetric === 'totalSales' 
                ? 'Total Sales' 
                : selectedMetric === 'growth' 
                  ? 'Growth Rate' 
                  : selectedMetric === 'productCount' 
                    ? 'Product Count' 
                    : 'Shop Count'
            }
            onClick={(data) => handleBarClick(data)} 
            cursor="pointer"
          />
        </BarChart>
      </ResponsiveContainer>
    );
  };
  
  // Render line chart (New addition)
  const renderLineChart = () => {
    const data = getCurrentLevelData();
    
    if (!data || data.length === 0) {
      return <div className="text-center py-12 text-gray-500">No data available for the selected categories</div>;
    }
    
    // For demo purposes, create time-series data for each item
    const timeSeriesData = [];
    
    // Generate 12 months of data for each category/product
    for (let month = 1; month <= 12; month++) {
      const monthData = {
        month: `Month ${month}`,
      };
      
      // Add data for each item
      data.forEach(item => {
        // Base value from the selected metric
        let baseValue = item[selectedMetric] || 0;
        
        // For demo: create a seasonal pattern with some randomness
        const seasonalFactor = 1 + 0.2 * Math.sin(month / 12 * 2 * Math.PI);
        const randomFactor = 0.95 + Math.random() * 0.1; // Random 0.95-1.05
        
        // Scale by month and add randomness
        const value = baseValue * seasonalFactor * randomFactor / 12;
        
        monthData[item.name] = value;
      });
      
      timeSeriesData.push(monthData);
    }
    
    return (
      <ResponsiveContainer width="100%" height={500}>
        <LineChart
          data={timeSeriesData}
          margin={{ top: 20, right: 30, left: 30, bottom: 10 }}
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="month" />
          <YAxis />
          <Tooltip formatter={(value) => 
            selectedMetric === 'totalSales' 
              ? `$${(value/1000000).toFixed(2)}M` 
              : selectedMetric === 'growth' 
                ? `${value.toFixed(1)}%` 
                : value
          } />
          <Legend />
          {data.map((item, index) => (
            <Line 
              key={item.id}
              type="monotone" 
              dataKey={item.name} 
              stroke={COLORS[index % COLORS.length]} 
              activeDot={{ r: 8, onClick: () => handleLineClick(item) }}
              strokeWidth={2}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    );
  };
  
  // Handle line chart click for drill down
  const handleLineClick = (item) => {
    // Find the matching category/product based on current level
    switch(currentLevel) {
      case 1:
        drillDown(item.id, null, null, null, 2);
        break;
      case 2:
        drillDown(selectedCategoryL1, item.id, null, null, 3);
        break;
      case 3:
        drillDown(selectedCategoryL1, selectedCategoryL2, item.id, null, 4);
        break;
      case 4:
        // At product level, we don't drill down further
        break;
    }
  };
  
  // Handle bar click for drill down
  const handleBarClick = (data) => {
    if (!data || !data.id) return;
    
    const clickedId = data.id;
    
    // Handle drill down based on current level
    switch(currentLevel) {
      case 1:
        drillDown(clickedId, null, null, null, 2);
        break;
      case 2:
        drillDown(selectedCategoryL1, clickedId, null, null, 3);
        break;
      case 3:
        drillDown(selectedCategoryL1, selectedCategoryL2, clickedId, null, 4);
        break;
      case 4:
        // At product level, we don't drill down further
        break;
    }
  };
  
  // Drill down to a specific level
  const drillDown = (l1Id, l2Id, l3Id, productId, level) => {
    // Update selections
    setSelectedCategoryL1(l1Id);
    setSelectedCategoryL2(l2Id);
    setSelectedCategoryL3(l3Id);
    setSelectedProduct(productId);
    
    // Update current level
    setCurrentLevel(level);
    
    // Update path history for breadcrumbs
    let newPath = [];
    
    // Add L1 if selected
    if (l1Id) {
      const l1Cat = productCategoriesL1.find(cat => cat.id === l1Id);
      if (l1Cat) {
        newPath.push({ id: l1Id, name: l1Cat.name, level: 1 });
      }
    }
    
    // Add L2 if selected
    if (l2Id) {
      const l2Cat = productCategoriesL2.find(cat => cat.id === l2Id);
      if (l2Cat) {
        newPath.push({ id: l2Id, name: l2Cat.name, level: 2 });
      }
    }
    
    // Add L3 if selected
    if (l3Id) {
      const l3Cat = productCategoriesL3.find(cat => cat.id === l3Id);
      if (l3Cat) {
        newPath.push({ id: l3Id, name: l3Cat.name, level: 3 });
      }
    }
    
    // Add product if selected
    if (productId) {
      const product = products.find(p => p.id === productId);
      if (product) {
        newPath.push({ id: productId, name: product.name, level: 4 });
      }
    }
    
    setPathHistory(newPath);
  };
  
  // Navigate up to a specific level
  const navigateUp = (targetLevel) => {
    switch(targetLevel) {
      case 1:
        drillDown(null, null, null, null, 1);
        break;
      case 2:
        drillDown(selectedCategoryL1, null, null, null, 2);
        break;
      case 3:
        drillDown(selectedCategoryL1, selectedCategoryL2, null, null, 3);
        break;
      default:
        // Default to level 1
        drillDown(null, null, null, null, 1);
    }
  };
  
  // Navigate directly to a specific point in the breadcrumb trail
  const navigateToBreadcrumb = (item) => {
    switch(item.level) {
      case 1:
        drillDown(item.id, null, null, null, 2);
        break;
      case 2:
        drillDown(selectedCategoryL1, item.id, null, null, 3);
        break;
      case 3:
        drillDown(selectedCategoryL1, selectedCategoryL2, item.id, null, 4);
        break;
      default:
        // Default stay at current level
        break;
    }
  };
  
  // Get title for current view
  const getCurrentTitle = () => {
    switch(currentLevel) {
      case 1:
        return 'Top-Level Categories';
      case 2: {
        const category = productCategoriesL1.find(cat => cat.id === selectedCategoryL1);
        return category ? `${category.name} Subcategories` : 'Subcategories';
      }
      case 3: {
        const category = productCategoriesL2.find(cat => cat.id === selectedCategoryL2);
        return category ? `${category.name} Detailed Categories` : 'Detailed Categories';
      }
      case 4: {
        const category = productCategoriesL3.find(cat => cat.id === selectedCategoryL3);
        return category ? `Products in ${category.name}` : 'Products';
      }
      default:
        return 'Product Categories';
    }
  };
  
  // Handle standard change - in POC, only display alert
  const handleStandardChange = (standardId) => {
    const standard = classificationStandards.find(s => s.id === parseInt(standardId));
    
    // For POC, show that other standards aren't available
    if (standard.id !== 1) {
      alert(`${standard.name} (${standard.code}) standard is not available in this POC version. Only GPC is currently implemented.`);
      return;
    }
    
    setSelectedStandard(standard);
  };
  
  // Render the visualization based on selected type
  const renderVisualization = () => {
    switch(visualizationType) {
      case 'piechart':
        return renderPieChart();
      case 'barchart':
        return renderBarChart();
      case 'linechart':
        return renderLineChart();
      default:
        return renderPieChart();
    }
  };
  
  return (
    <div className="bg-white p-6 rounded-lg shadow-sm">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800 mb-4">Product Category Hierarchy</h1>
        <p className="text-gray-600 mb-4">
          Explore the hierarchical structure of product categories. Drill down by clicking on chart segments or navigate up using the breadcrumb trail.
        </p>
        
        {/* Product Classification Standard Selector */}
        <div className="bg-gray-50 p-4 border rounded-md mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-xl font-semibold text-blue-800 mb-3">Product Classification Standard</h3>
              <p className="text-gray-700 mb-3">Currently using: {selectedStandard.name} ({selectedStandard.code}) v{selectedStandard.version}</p>
            </div>
            <div className="w-64">
              <select 
                value={selectedStandard.id}
                onChange={(e) => handleStandardChange(e.target.value)}
                className="dropdown-selector"
              >
                {classificationStandards.map(standard => (
                  <option 
                    key={standard.id} 
                    value={standard.id}
                    disabled={!standard.isActive}
                  >
                    {standard.code} - {standard.name} {!standard.isActive && '(Not Available in POC)'}
                  </option>
                ))}
              </select>
              <p className="text-xs text-gray-500 mt-1">Note: Only GPC standard is available in this POC version</p>
            </div>
          </div>
        </div>
        
        {/* Enhanced Navigation breadcrumbs - Now using the PathNavigation component */}
        <PathNavigation 
          pathHistory={pathHistory}
          navigateUp={navigateUp}
          navigateToBreadcrumb={navigateToBreadcrumb}
          currentLevel={currentLevel}
        />
        
        {/* Control panel - Metric Selector */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div>
            <label className="block text-sm font-medium text-gray-600 mb-3">Metric</label>
            <select 
              value={selectedMetric}
              onChange={(e) => setSelectedMetric(e.target.value)}
              className="dropdown-selector"
            >
              <option value="totalSales">Total Sales (USD)</option>
              <option value="growth">Growth Rate (%)</option>
              <option value="productCount">Product Count</option>
              <option value="shopCount">Shop Count</option>
            </select>
          </div>
          
          <div className="flex items-end">
            <button
              onClick={() => navigateUp(currentLevel - 1)}
              disabled={currentLevel === 1}
              className={`px-4 py-2 rounded-md font-medium text-sm ${
                currentLevel === 1 
                  ? 'bg-gray-200 text-gray-400 cursor-not-allowed' 
                  : 'bg-blue-600 text-white hover:bg-blue-700'
              }`}
            >
              ← Back Up One Level
            </button>
          </div>
        </div>
        
        {/* Level indicator */}
        <div className="bg-blue-50 p-3 rounded-md mb-4">
          <p className="text-blue-800 font-medium">
            Current View: <span className="font-bold">{getCurrentTitle()}</span>
            {currentLevel > 1 && (
              <span className="text-blue-600 ml-2 text-sm">
                (Click on chart elements to drill down, use breadcrumbs to navigate)
              </span>
            )}
          </p>
        </div>
        
        {/* Visualization controls - Now positioned closer to the graph */}
        <div className="border border-gray-200 rounded-lg p-4">
          <div className="mb-3">
            <VisualizationControls 
              visualizationType={visualizationType}
              setVisualizationType={setVisualizationType}
            />
          </div>
          
          {/* Visualization area */}
          {renderVisualization()}
        </div>
      </div>
      
      {/* Data table for current level */}
      <div className="mt-6">
        <h2 className="text-lg font-semibold text-gray-800 mb-3">
          {getCurrentTitle()}
        </h2>
        <div className="overflow-x-auto">
          <table className="table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Total Sales</th>
                <th>Growth</th>
                <th>{currentLevel === 4 ? 'Launch Year' : 'Product Count'}</th>
                {currentLevel !== 4 && <th>Shop Count</th>}
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {getCurrentLevelData().map(item => (
                <tr key={item.id}>
                  <td>{item.name}</td>
                  <td>${(item.totalSales / 1000000).toFixed(2)}M</td>
                  <td className={item.growth >= 0 ? 'positive-growth' : 'negative-growth'}>
                    {item.growth >= 0 ? '↑' : '↓'} {Math.abs(item.growth).toFixed(1)}%
                  </td>
                  <td>{currentLevel === 4 ? item.launchYear : item.productCount}</td>
                  {currentLevel !== 4 && <td>{item.shopCount}</td>}
                  <td>
                    {currentLevel < 4 && (
                      <button
                        onClick={() => handlePieClick({ payload: { id: item.id, name: item.name } })}
                        className="px-3 py-1 bg-blue-100 hover:bg-blue-200 text-blue-800 rounded-md text-xs"
                      >
                        Drill Down
                      </button>
                    )}
                    {currentLevel === 4 && (
                      <span className="text-xs text-gray-500">Final Product</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      
      {/* Key Features Section */}
      <h2 className="text-2xl font-bold text-gray-800 mt-8 mb-4">Key Features</h2>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        <div className="bg-blue-50 p-6 rounded-lg border border-blue-100">
          <h3 className="text-xl font-semibold text-blue-800 mb-3">All Categories</h3>
          <p className="text-gray-700 mb-3">
            Click on any level to navigate directly to it. Explore the hierarchical structure from high-level 
            categories down to individual products and discover performance patterns at every level.
          </p>
          <div className="mt-4">
            <span className="text-blue-600 font-medium">
              Try Product Hierarchy →
            </span>
          </div>

        </div>
        
        <div className="bg-green-50 p-6 rounded-lg border border-green-100">
          <h3 className="text-xl font-semibold text-green-800 mb-3">Navigation Tips</h3>
          <p className="text-gray-700 mb-3">
            Use the breadcrumb trail to navigate back up the hierarchy. Click on chart segments to drill down 
            or use the table actions to explore subcategories and products.
          </p>
          <div className="mt-4">
            <span className="text-green-600 font-medium">
              View Categories →
            </span>
          </div>

        </div>
      </div>

    </div>
  );
};

export default ProductHierarchy;
